import { useEffect } from "react";
import { createPortal } from "react-dom";
import { ImageThumbnail } from "./ImageThumbnail";

interface ImageLightboxProps {
  readonly imageId: string;
  readonly onClose: () => void;
}

export function ImageLightbox({ imageId, onClose }: ImageLightboxProps) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
      style={{ isolation: "isolate" }}
      data-ph-no-capture
    >
      {/* Backdrop — full screen dark overlay */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm" />

      {/* Image container — centered with padding */}
      <div
        className="relative z-[1] flex items-center justify-center p-10"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative rounded-xl overflow-visible shadow-2xl ring-1 ring-white/10">
          {/* Close button — top-right of image */}
          <button
            type="button"
            onClick={onClose}
            className="absolute -top-3 -right-3 z-10 w-8 h-8 flex items-center justify-center
              rounded-full bg-black/70 text-white/80 hover:bg-black hover:text-white
              transition-colors border border-white/20 shadow-lg"
            aria-label="Fermer"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4" stroke="currentColor" strokeWidth={2.5} fill="none">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
          <div className="rounded-xl overflow-hidden">
            <ImageThumbnail
              imageId={imageId}
              alt="Image plein écran"
              className="max-h-[80vh] max-w-[85vw] w-auto h-auto object-contain"
            />
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
